import { section, p, list, steps, tip, warn, table, faq } from './blocks';

export default {
  slug: 'personal-schichtplanung',
  title: 'Personal- und Schichtplanung',
  shortTitle: 'Personal & Schichtplanung',
  icon: 'Users',
  updated: 'Juni 2026',
  datePublished: '2026-06-24',
  dateModified: '2026-06-24',
  image: 'https://images.pexels.com/photos/18999381/pexels-photo-18999381.jpeg?auto=compress&cs=tinysrgb&w=1200&h=900&fit=crop',
  teaser: 'Digitale Dienstpläne, Zeiterfassung und Mitarbeiter-App: Wie du Schichten bedarfsgerecht planst, gesetzliche Pflichten erfüllst und dein Team entlastest.',
  mainArticle: {
    title: 'Digitale Personal- und Schichtplanung: Der große Leitfaden',
    takeaways: [
      'Digitale Planung spart Führungskräften fünf bis zehn Stunden pro Woche und reduziert Lohnfehler.',
      'Arbeitszeitgesetz, Pausen und Aufzeichnungspflicht lassen sich nur digital zuverlässig einhalten.',
      'Planung nach Umsatzprognose statt fester Besetzung senkt die Personalkostenquote.',
      'Akzeptanz entsteht durch Nutzen: Plan auf dem Handy, Schichttausch ohne Anruf beim Chef.',
    ],
    intro: 'Personal ist in der Gastronomie der größte Kostenblock und gleichzeitig der wichtigste Erfolgsfaktor. Wer Schichten noch in Tabellen plant und Änderungen per Messenger verteilt, verliert Zeit, Überblick und im schlimmsten Fall Mitarbeiter. Dieser Leitfaden zeigt, wie digitale Personalplanung funktioniert und worauf du achten solltest.',
    sections: [
      section(
        'Warum Tabelle und Messenger-Gruppe nicht mehr reichen',
        p('Der klassische Dienstplan entsteht am Sonntagabend in einer Tabelle, wird ausgedruckt, fotografiert und in die Team-Gruppe gestellt. Dann beginnen die Änderungen: Jemand wird krank, zwei tauschen die Schicht, ein Aushilfe fragt nach ihren Stunden. Am Monatsende versucht jemand, aus Zetteln und Nachrichten die Arbeitszeiten für die Lohnabrechnung zu rekonstruieren.'),
        p('Dieses Vorgehen kostet Führungskräfte in vielen Betrieben **fünf bis zehn Stunden pro Woche** – und produziert trotzdem Fehler bei Stunden, Zuschlägen und Urlaub. Digitale Planungstools lösen das, indem Plan, Verfügbarkeit, Zeiterfassung und Lohnexport in einem System zusammenlaufen.')
      ),
      section(
        'Was ein digitales Planungstool kann',
        list([
          '**Dienstplan per Drag & Drop** mit Vorlagen für wiederkehrende Wochen',
          '**Verfügbarkeiten und Wünsche** der Mitarbeiter direkt in der App erfassen',
          '**Schichttausch** zwischen Mitarbeitern mit Freigabe durch die Leitung',
          '**Zeiterfassung** per App, Terminal oder Kassen-Login inklusive Pausen',
          '**Urlaubs- und Abwesenheitsverwaltung** mit Anträgen, Genehmigung und Resturlaub',
          '**Personalkosten-Vorschau** direkt bei der Planung – inklusive Zuschlägen',
          '**Warnungen** bei Verstößen gegen Arbeitszeitgesetz, Ruhezeiten oder Stundenkontingente',
          '**Lohnexport** an Steuerberater oder Lohnbuchhaltung (z. B. DATEV-Format)',
          '**Mehrere Standorte und Abteilungen** mit eigenen Plänen und Rechten',
        ])
      ),
      section(
        'Gesetzliche Rahmenbedingungen',
        p('Die Personalplanung in der Gastronomie ist stark reguliert. Ein digitales System hilft, die Regeln einzuhalten – die Verantwortung bleibt aber bei dir. Die wichtigsten Punkte:'),
        list([
          '**Arbeitszeitgesetz:** Maximal 8 Stunden täglich (bis 10 Stunden mit Ausgleich), in der Gastronomie unter Bedingungen bis 10 Stunden; Ruhezeit von mindestens 11 Stunden zwischen zwei Schichten (in der Gastronomie unter Bedingungen 10 Stunden).',
          '**Pausen:** Ab 6 Stunden 30 Minuten, ab 9 Stunden 45 Minuten – spätestens nach 6 Stunden.',
          '**Aufzeichnungspflicht:** Beginn, Ende und Dauer der täglichen Arbeitszeit müssen dokumentiert werden – für Minijobber und in der Gastronomie ohnehin nach dem Mindestlohngesetz, nach der Rechtsprechung des Bundesarbeitsgerichts grundsätzlich für alle Beschäftigten.',
          '**Sonn- und Feiertagsarbeit:** In der Gastronomie erlaubt, aber mit Ersatzruhetagen und Mindestanzahl freier Sonntage.',
          '**Jugendarbeitsschutz:** Eigene Regeln für Beschäftigte unter 18 Jahren.',
          '**Minijob-Grenzen:** Verdienstgrenze und Stundenkontingent im Blick behalten.',
        ]),
        warn('Diese Übersicht dient der Orientierung. Tarifverträge, Betriebsvereinbarungen und Sonderregelungen können abweichen. Kläre die für dich geltenden Regeln mit Steuerberater oder Arbeitsrechtler.')
      ),
      section(
        'Integration mit Kasse, Buchhaltung und Reservierung',
        p('Der eigentliche Mehrwert entsteht, wenn die Personalplanung mit anderen Systemen verbunden ist:'),
        list([
          '**Kasse → Planung:** Umsätze nach Stunde und Wochentag zeigen, wann wirklich Personal gebraucht wird. Daraus entstehen Bedarfsprognosen statt Bauchgefühl.',
          '**Reservierung → Planung:** Die Reservierungslage der nächsten Tage fließt in die Bedarfsberechnung ein.',
          '**Planung → Lohnbuchhaltung:** Erfasste Stunden, Zuschläge und Abwesenheiten gehen digital an den Steuerberater – kein Abtippen mehr.',
          '**Planung → Reporting:** Die Personalkostenquote wird täglich statt einmal im Monat sichtbar.',
        ])
      ),
      section(
        'Was kostet digitale Personalplanung?',
        p('Die meisten Tools werden **pro Mitarbeiter und Monat** abgerechnet, üblich sind etwa 2 – 6 € je aktivem Mitarbeiter. Ein Betrieb mit 25 Mitarbeitern zahlt damit rund 50 – 150 € im Monat. Zeiterfassungsterminals kosten einmalig 200 – 600 €, sind aber optional – die Erfassung per App oder Kassen-Login ist meist inklusive.'),
        p('Dem gegenüber steht die Zeitersparnis: Wenn eine Führungskraft fünf Stunden pro Woche weniger mit Planung und Stundenrekonstruktion verbringt, sind das bei 25 €/Stunde rund 500 € im Monat – ohne die vermiedenen Fehler bei der Lohnabrechnung.')
      ),
      section(
        'Worauf du bei der Auswahl achten solltest',
        steps([
          '**Bedienbarkeit für das Team:** Die App muss auch für Aushilfen ohne Einführung verständlich sein – sonst wird sie nicht genutzt.',
          '**Zeiterfassungsmethode:** App mit Standortprüfung, Terminal, Kassen-Login oder Chipkarte – was passt zu deinem Betrieb?',
          '**Gesetzeskonformität:** Warnt das System bei Verstößen gegen Ruhezeiten und Höchstarbeitszeiten?',
          '**Lohnexport:** Gibt es eine Schnittstelle oder ein Exportformat, das dein Steuerberater direkt einlesen kann?',
          '**Kassenanbindung:** Können Umsatzdaten für die Bedarfsplanung importiert werden?',
          '**Rechte und Rollen:** Schichtleiter, Abteilungsleiter, Inhaber – wer darf was sehen und ändern?',
          '**Datenschutz:** Serverstandort, AVV, Umgang mit Standortdaten bei App-Erfassung.',
        ])
      ),
      section(
        'Einführung im Team',
        p('Der Erfolg hängt davon ab, ob das Team mitzieht. Erkläre den Nutzen aus Mitarbeitersicht: Dienstplan immer auf dem Handy, Schichttausch ohne Chef-Anruf, transparente Stundenkonten, kein Streit über Überstunden. Starte mit dem Dienstplan und ergänze Zeiterfassung und Urlaubsanträge nach einigen Wochen, wenn die Nutzung selbstverständlich ist.'),
        tip('Lege wenige, klare Regeln fest: Bis wann werden Verfügbarkeiten eingetragen? Wie lange vorher steht der Plan? Wie läuft ein Schichttausch? Diese Regeln in der App zu hinterlegen, nimmt viele Diskussionen vorweg.')
      ),
      section(
        'Stolperfallen bei der Einführung',
        list([
          '**Planung nach Köpfen statt nach Bedarf:** Wer immer dieselbe Besetzung plant, hat dienstags zu viel und samstags zu wenig Personal.',
          '**Zeiterfassung ohne Pausenregelung:** Pausen müssen erfasst oder automatisch abgezogen werden – sonst stimmen die Stunden nicht.',
          '**Kein Korrekturprozess:** Vergessene Stempelungen brauchen einen definierten Weg zur Korrektur mit Freigabe.',
          '**Parallelstrukturen:** Wenn Änderungen weiter über den Messenger laufen, verliert das System seine Verlässlichkeit.',
          '**Datenschutz ignorieren:** Standortdaten bei der App-Erfassung nur so weit nutzen, wie es nötig und kommuniziert ist.',
        ])
      ),
      section(
        'Was Gastronomen uns dazu fragen',
        faq([
          { q: 'Muss ich die Arbeitszeit meiner Mitarbeiter wirklich elektronisch erfassen?', a: 'Die Aufzeichnungspflicht besteht – in der Gastronomie schon lange über das Mindestlohngesetz und nach der Rechtsprechung des Bundesarbeitsgerichts grundsätzlich für alle Beschäftigten. Die Form ist bislang nicht zwingend elektronisch vorgeschrieben, digitale Erfassung ist aber der einfachste und sicherste Weg.' },
          { q: 'Was ist, wenn Mitarbeiter kein Smartphone nutzen wollen?', a: 'Die meisten Systeme bieten Alternativen: ein Terminal oder Tablet im Betrieb zum Stempeln, Aushang des Plans oder Erfassung durch die Schichtleitung. Wichtig ist, dass alle Wege in dasselbe System münden.' },
          { q: 'Wie weit im Voraus sollte der Dienstplan stehen?', a: 'Empfehlenswert sind mindestens zwei Wochen, besser drei bis vier. Planbarkeit ist einer der wichtigsten Faktoren für Mitarbeiterzufriedenheit in der Gastronomie.' },
          { q: 'Kann der Steuerberater die Daten direkt übernehmen?', a: 'Bei den meisten Tools ja – über Standard-Exportformate oder direkte Schnittstellen zur gängigen Lohnsoftware. Kläre vorab mit deiner Kanzlei, welches Format sie benötigt.' },
        ])
      ),
    ],
  },
  subArticles: [
    {
      slug: 'digitale-zeiterfassung',
      title: 'Digitale Zeiterfassung: Pflichten, Methoden und Umsetzung',
      shortTitle: 'Digitale Zeiterfassung',
      teaser: 'Welche Aufzeichnungspflichten gelten, welche Erfassungsmethoden es gibt und wie du Zeiterfassung fair und manipulationssicher einführst.',
      intro: 'Zeiterfassung ist kein Misstrauensinstrument, sondern schafft Klarheit für beide Seiten: Mitarbeiter wissen, dass jede Stunde zählt, und du hast belastbare Zahlen für Lohnabrechnung und Planung. So setzt du sie richtig um.',
      sections: [
        section(
          'Die rechtlichen Grundlagen kurz erklärt',
          p('In der Gastronomie besteht nach dem Mindestlohngesetz seit Jahren die Pflicht, Beginn, Ende und Dauer der täglichen Arbeitszeit aufzuzeichnen – spätestens bis zum siebten Kalendertag nach dem Arbeitstag. Das Bundesarbeitsgericht hat darüber hinaus klargestellt, dass Arbeitgeber grundsätzlich verpflichtet sind, ein System zur Erfassung der Arbeitszeit einzuführen. Die Aufzeichnungen sind mindestens zwei Jahre aufzubewahren.'),
          p('Die Form ist nicht zwingend elektronisch vorgeschrieben. In der Praxis ist die digitale Erfassung jedoch der einzige Weg, der ohne Zettelwirtschaft funktioniert und Zuschläge, Pausen und Überstunden automatisch korrekt berechnet.')
        ),
        section(
          'Erfassungsmethoden im Vergleich',
          table(
            ['Methode', 'Vorteile', 'Nachteile', 'Geeignet für'],
            [
              ['App auf dem eigenen Smartphone', 'Keine Hardware, Standortprüfung möglich', 'Setzt Smartphone voraus, Datenschutz beachten', 'Kleine bis mittlere Teams'],
              ['Terminal / Tablet im Betrieb', 'Zentral, für alle nutzbar, mit PIN oder Chip', 'Anschaffung, Standort im Betrieb nötig', 'Betriebe mit Aushilfen ohne Smartphone-Nutzung'],
              ['Login in der Kasse', 'Keine zusätzliche Hardware, an bestehende Routine gekoppelt', 'Nur für Personal mit Kassenzugang, Küche oft außen vor', 'Service-Teams'],
              ['Manuelle Erfassung durch Leitung', 'Einfach', 'Fehleranfällig, kein Nachweis der Eigenerfassung', 'Übergangslösung'],
            ]
          )
        ),
        section(
          'Anforderungen an ein gutes System',
          list([
            '**Pausenerfassung** – manuell durch den Mitarbeiter oder automatisch nach gesetzlichen Vorgaben',
            '**Korrekturprozess** mit Antrag durch den Mitarbeiter und Freigabe durch die Leitung, inklusive Protokoll',
            '**Warnungen** bei fehlenden Stempelungen, überschrittenen Höchstarbeitszeiten und unterschrittenen Ruhezeiten',
            '**Zuschlagsberechnung** für Nacht-, Sonntags- und Feiertagsarbeit',
            '**Stundenkonten** mit Einblick für den Mitarbeiter',
            '**Export** für die Lohnabrechnung im passenden Format',
          ])
        ),
        section(
          'Datenschutz bei der Zeiterfassung',
          p('Arbeitszeitdaten sind personenbezogene Daten. Erfasse nur, was nötig ist: Bei App-basierter Erfassung reicht die Prüfung, ob der Mitarbeiter sich im Betrieb befindet – eine dauerhafte Standortverfolgung ist unzulässig. Informiere das Team schriftlich darüber, welche Daten erfasst und wie lange sie gespeichert werden. Bei einem Betriebsrat ist die Einführung mitbestimmungspflichtig.'),
          tip('Gib jedem Mitarbeiter Einblick in das eigene Stundenkonto. Transparenz nimmt der Zeiterfassung den Kontroll-Charakter und reduziert Diskussionen am Monatsende drastisch.')
        ),
        section(
          'Einführung in fünf Schritten',
          steps([
            'Methode wählen und Hardware bzw. App bereitstellen',
            'Pausenregelung, Rundungsregeln und Korrekturprozess schriftlich festlegen',
            'Team informieren – Nutzen erklären, Fragen klären, Datenschutzhinweis aushändigen',
            'Zwei Wochen Testphase mit täglicher Kontrolle auf fehlende Stempelungen',
            'Erste Lohnabrechnung gemeinsam mit dem Steuerberater auf Basis der digitalen Daten prüfen',
          ]),
          p('Hinweis: Dieser Artikel dient der Orientierung und ersetzt keine arbeitsrechtliche Beratung.')
        ),
      ],
    },
    {
      slug: 'personalkosten-umsatzprognose',
      title: 'Personalkostenquote im Griff: Schichtplanung nach Umsatzprognose',
      shortTitle: 'Personalkosten & Prognose',
      teaser: 'Wie du aus Kassendaten den tatsächlichen Personalbedarf ableitest, in Stunden statt Köpfen planst und die Personalkostenquote steuerst.',
      intro: 'Zu viel Personal am Dienstagmittag, zu wenig am Freitagabend – dieses Muster kennt fast jeder Betrieb. Die Lösung liegt in deinen eigenen Daten: Wer Umsatzverläufe kennt, kann Personal dort einsetzen, wo es gebraucht wird.',
      sections: [
        section(
          'Die Personalkostenquote als Steuerungsgröße',
          p('Die Personalkostenquote setzt die gesamten Personalkosten (Löhne, Gehälter, Sozialabgaben, Aushilfen) ins Verhältnis zum Nettoumsatz. Je nach Konzept liegen gesunde Werte zwischen etwa **25 und 35 Prozent** – Schnellgastronomie eher niedriger, Fine Dining eher höher. Entscheidend ist weniger der absolute Wert als die Entwicklung über die Zeit und der Vergleich zwischen Wochentagen und Schichten.')
        ),
        section(
          'Umsatzprognose aus Kassendaten',
          p('Deine Kasse weiß, wie viel Umsatz du an jedem Wochentag in jeder Stunde gemacht hast. Aus den letzten 8 – 12 Wochen lässt sich ein verlässliches Muster ableiten. Ergänze dieses Muster um bekannte Einflussfaktoren:'),
          list([
            'Reservierungslage der kommenden Tage',
            'Wetterprognose (besonders bei Außenflächen)',
            'Feiertage, Ferien, lokale Events und Messen',
            'Eigene Aktionen und Veranstaltungen',
            'Saisonale Trends aus dem Vorjahr',
          ]),
          p('Viele Planungstools importieren Kassendaten automatisch und zeigen die Prognose direkt neben dem Dienstplan. Auch eine einfache Tabelle mit Durchschnittswerten je Wochentag und Stunde ist ein guter Anfang.')
        ),
        section(
          'Vom Umsatz zum Personalbedarf',
          p('Die Brücke zwischen Umsatz und Bedarf ist die **Produktivität**: Umsatz pro geleisteter Arbeitsstunde. Rechne aus, wie viel Umsatz eine Servicekraft bzw. eine Küchenstunde in deinem Betrieb typischerweise erwirtschaftet, und leite daraus den Bedarf ab.'),
          table(
            ['Zeitfenster', 'Prognose-Umsatz', 'Zielproduktivität', 'Bedarf (Stunden)', 'Geplant', 'Abweichung'],
            [
              ['Di 11 – 14 Uhr', '900 €', '75 €/Std.', '12', '15', '+3 (zu viel)'],
              ['Fr 18 – 23 Uhr', '4.500 €', '75 €/Std.', '60', '50', '−10 (zu wenig)'],
              ['Sa 18 – 23 Uhr', '5.200 €', '75 €/Std.', '69', '65', '−4'],
            ]
          ),
          p('Wichtig: Plane **Stunden, nicht Köpfe**. Statt drei Personen von 17 bis 24 Uhr können zwei um 17 Uhr beginnen und eine dritte um 18:30 Uhr dazukommen – gleiche Servicequalität, spürbar weniger Kosten.')
        ),
        section(
          'Mindestbesetzung und Qualität',
          p('Die Rechnung hat Grenzen: Auch am ruhigsten Dienstag brauchst du eine Mindestbesetzung für Öffnung, Küche und Sicherheit. Definiere diese Untergrenze pro Bereich und plane darunter nie. Ebenso solltest du in Stoßzeiten nicht am Personal sparen, wenn dadurch Service leidet und Gäste nicht wiederkommen – Umsatzverlust ist teurer als eine Stunde Personal.'),
          tip('Vergleiche wöchentlich Prognose, Plan und Ist. Nach vier bis sechs Wochen erkennst du Muster – etwa, dass die Küche freitags systematisch zu spät hochfährt – und kannst Vorlagen entsprechend anpassen.')
        ),
        section(
          'Typische Fehler',
          list([
            'Planung nach festen Wochenvorlagen ohne Blick auf die Prognose',
            'Umsatz in Prognose brutto, Personalkosten netto – Quoten werden dadurch verzerrt',
            'Aushilfen und Inhaberarbeitszeit nicht mitgerechnet',
            'Keine Trennung zwischen Service- und Küchenproduktivität',
            'Kosten senken, aber Servicequalität nicht messen (Bewertungen, Wartezeiten)',
          ])
        ),
      ],
    },
    {
      slug: 'mitarbeiter-app-kommunikation',
      title: 'Mitarbeiter-App und Teamkommunikation: Schluss mit dem Messenger-Chaos',
      shortTitle: 'Mitarbeiter-App',
      teaser: 'Dienstplan, Schichttausch, Urlaubsanträge und Ankündigungen an einem Ort: Warum eine Mitarbeiter-App die Team-Gruppe im Messenger ersetzen sollte.',
      intro: 'Die Team-Gruppe im Messenger ist schnell eingerichtet – und schnell außer Kontrolle. Wichtige Infos gehen zwischen Memes und Krankmeldungen verloren, private Nummern werden geteilt, und wer nicht in der Gruppe ist, erfährt nichts. Eine Mitarbeiter-App schafft Struktur.',
      sections: [
        section(
          'Warum der Messenger problematisch ist',
          list([
            '**Datenschutz:** Private Telefonnummern aller Mitarbeiter sind für alle sichtbar; Krankmeldungen und persönliche Daten laufen über Server außerhalb deiner Kontrolle.',
            '**Verbindlichkeit:** Niemand weiß, welche Nachricht die aktuelle Planversion ist.',
            '**Grenzen verschwimmen:** Dienstliche Nachrichten um 23 Uhr im privaten Chat belasten das Team.',
            '**Keine Struktur:** Schichttausch, Urlaubswunsch und Wochenplan stehen ungeordnet untereinander.',
            '**Ausgeschiedene Mitarbeiter** bleiben oft monatelang in der Gruppe.',
          ])
        ),
        section(
          'Was eine Mitarbeiter-App löst',
          p('Moderne Personalplanungstools enthalten eine App für das Team, die alle arbeitsbezogenen Themen bündelt:'),
          list([
            '**Dienstplan** immer aktuell, mit Push-Benachrichtigung bei Änderungen',
            '**Verfügbarkeiten** und Schichtwünsche eintragen',
            '**Schichttausch** anfragen – Kollegen sehen offene Schichten, die Leitung gibt frei',
            '**Urlaubs- und Abwesenheitsanträge** mit Status und Resturlaub',
            '**Stundenkonto** und Zeiterfassung',
            '**Ankündigungen** der Leitung mit Lesebestätigung',
            '**Dokumente**: Speisekarte, Allergenliste, Hygieneplan, Onboarding-Unterlagen',
          ])
        ),
        section(
          'Regeln für die Teamkommunikation',
          p('Ein Tool allein ändert keine Gewohnheiten. Lege gemeinsam mit dem Team fest, welche Kommunikation wo stattfindet:'),
          table(
            ['Thema', 'Kanal', 'Regel'],
            [
              ['Dienstplan, Tausch, Urlaub', 'Mitarbeiter-App', 'Ausschließlich hier – gilt als verbindlich'],
              ['Krankmeldung', 'Anruf bei Schichtleitung + Eintrag App', 'Bis spätestens 2 Stunden vor Schichtbeginn'],
              ['Ankündigungen der Leitung', 'App mit Lesebestätigung', 'Keine Diskussionen im Ankündigungskanal'],
              ['Kurzfristiges im Service', 'Persönlich / Schichtübergabe', 'Nicht per Nachricht während der Schicht'],
              ['Privates, Teamevents', 'Freiwillige Gruppe', 'Keine dienstlichen Inhalte'],
            ]
          )
        ),
        section(
          'Onboarding neuer Mitarbeiter',
          p('Die App ist auch ein Onboarding-Werkzeug. Neue Mitarbeiter erhalten Zugang, sehen ihre ersten Schichten, finden Speisekarte, Ablaufpläne und Ansprechpartner an einem Ort und können Fragen strukturiert stellen. Das entlastet die Schichtleitung am ersten Arbeitstag und sorgt dafür, dass wichtige Infos nicht mündlich verloren gehen.'),
          tip('Erstelle eine kurze Checkliste „Erste Woche“ als Dokument in der App: Wer zeigt was, welche Unterlagen fehlen, wann ist das erste Feedbackgespräch? So läuft jedes Onboarding gleich.')
        ),
        section(
          'Akzeptanz im Team sichern',
          list([
            'Nutzen aus Mitarbeitersicht erklären: Planbarkeit, Transparenz, weniger Nachrichten',
            'Die Leitung nutzt die App konsequent selbst – keine Ausnahmen über den Messenger',
            'Kurze Einführung für alle, Ansprechperson für Fragen benennen',
            'Nach vier Wochen Feedback einholen und Regeln nachschärfen',
            'Ausgeschiedene Mitarbeiter sofort deaktivieren',
          ])
        ),
      ],
    },
  ],
};
